import { DateOnly, isAfter } from '@if/core/date'
import {
  EFinancingProgram,
  EOriginSystemId,
  EPaymentFrequency,
  EPaymentPlan,
  PlanPromoRule,
} from '@if/core/types'
import { formatDate, formatUtcIsoDateTime } from '@if/core/utils'
import {
  computeTotalObligation,
  TotalObligationComputedInfo,
} from '@src/containers/ViewWorksheet/viewWorksheet-selectors'
import { useGetFirstPaymentDateOptions } from '@src/data/api/credit-application/credit-application-api'
import { usePlanEnumForProgram } from '@src/data/selectors/app-config-selectors'
import { TranslatedEnum } from '@src/data/types/TranslatedEnum'
import { formatPaymentPlanForList } from '@src/services/Formatter'
import { useEffect, useMemo } from 'react'
import { FieldPath, UseFormGetValues, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Constants, CreditApplication } from '../../data/types'
import { MerchantPaymentPlan } from '../../data/types/MerchantPaymentPlan'

export { useAprTooHigh } from '@src/components/WorksheetForm/BaseWorksheetForm.helpers'

export const usePaymentPlanList = (
  createdOn?: Date,
  paymentPlans?: MerchantPaymentPlan[],
  disabledPromo?: Set<string>,
  currentPaymentPlanId?: string,
  planPromoRules?: PlanPromoRule[],
) => {
  const { t } = useTranslation()

  const isWithinInterval = (date: Date, interval: { start: Date; end: Date }): boolean => {
    if (!date || !interval?.start || !interval.end || interval.end <= interval.start) return false
    return date >= interval.start && date <= interval.end
  }
  const ret = useMemo(() => {
    const selectValueList: { label: string; value: string; disabled: boolean }[] = []

    selectValueList.push({
      value: EPaymentPlan.RegularDailyInterests,
      label: t('EPaymentPlan.regularInterest', { ns: 'enum' }),
      disabled: false,
    })

    const currentDate = new Date()
    const creationDate = createdOn ? new Date(createdOn) : null
    const DISTANT_FUTURE = new Date('2099-12-31T23:59:59.999Z')

    let highestPlanLevel: PlanPromoRule | undefined = undefined
    let maxStars = 0

    if (planPromoRules && planPromoRules.length > 0) {
      for (const rule of planPromoRules) {
        const starCount = rule.planName.length
        if (starCount > maxStars) {
          maxStars = starCount
          highestPlanLevel = rule
        }
      }
    }

    const filteredPaymentPlans = paymentPlans?.filter((plan) => {
      const planActiveFrom = new Date(plan.activeFrom)
      const planExpiresOn = new Date(plan?.expiresOn ?? DISTANT_FUTURE)
      const currentUtcDateFormatted = new Date(formatUtcIsoDateTime(new Date()))

      const isDateValid =
        ((creationDate && isWithinInterval(creationDate, { start: planActiveFrom, end: planExpiresOn })) ||
          isWithinInterval(currentDate, { start: planActiveFrom, end: planExpiresOn })) &&
        isAfter(planExpiresOn, currentUtcDateFormatted)

      let isPromoCodeValid = true
      if (highestPlanLevel && highestPlanLevel.selectablePromoCodes.length > 0) {
        const promoCode = plan.promoCode.split(' ')[0]
        isPromoCodeValid =
          highestPlanLevel.selectablePromoCodes.includes(promoCode) || promoCode === highestPlanLevel.defaultPromoCode
      }

      return (isDateValid && isPromoCodeValid) || currentPaymentPlanId === plan.id
    })

    filteredPaymentPlans?.forEach((p) => {
      selectValueList.push({
        value: p.id,
        label: formatPaymentPlanForList(p),
        disabled: !!disabledPromo?.has(p.promoCode.split(' ')[0]),
      })
    })

    return selectValueList
  }, [createdOn, disabledPromo, paymentPlans, t, planPromoRules, currentPaymentPlanId])

  return ret
}

export const usePossibleTerms = (maxTermDuration: number | null | undefined, isVariable: boolean) => {
  const ret = useMemo(() => {
    let supportedTerms = [12, 18, 24, 36, 48, 60, 72, 84]
    if (isVariable) supportedTerms.push(6)
    supportedTerms = supportedTerms.filter((val) => val <= (maxTermDuration ?? 0))
    return supportedTerms
  }, [isVariable, maxTermDuration])

  return ret
}

// Backend parity (EditCreditRslaWorksheetDtoValidator): Gold=12, Silver=24/36, Bronze=12, no/unknown plan=12.
export const useCreditRslaTermsByPlanName = (planName?: string | null): number[] => {
  const creditRslaPlanEnum = usePlanEnumForProgram(EFinancingProgram.CreditRsla) as Record<
    string,
    TranslatedEnum | undefined
  >
  const gold = creditRslaPlanEnum.creditRslaGold
  const silver = creditRslaPlanEnum.creditRslaSilver
  const bronze = creditRslaPlanEnum.creditRslaBronze

  const termsByPlan = useMemo(() => {
    const map: Record<string, number[]> = {}
    if (gold) map[gold.id] = [12]
    if (silver) map[silver.id] = [24, 36]
    if (bronze) map[bronze.id] = [12]
    return map
  }, [gold, silver, bronze])

  return useMemo(() => termsByPlan[planName ?? ''] || [12], [planName, termsByPlan])
}

export const useCreditRslaTerms = (creditApplication: CreditApplication): number[] =>
  useCreditRslaTermsByPlanName(creditApplication.applicant.creditDecision?.planName)

export const useEditTotalObligation = (
  amountRequested: number,
  computedInfo?: TotalObligationComputedInfo,
): number =>
  useMemo(
    () => computeTotalObligation(amountRequested, computedInfo),
    [amountRequested, computedInfo],
  )

export const useProductsTerms = (maxTermDuration: number) => {
  const productsTerms = Constants.financingProgramConfigs[EFinancingProgram.Products].supportedTerms
  return useMemo(
    () => productsTerms.filter((term: number) => term <= maxTermDuration),
    [maxTermDuration, productsTerms],
  )
}

interface FirstPaymentField {
  firstPaymentOn?: string
}
interface UseFirstPaymentDateOptionsParams<T extends FirstPaymentField> {
  financingProgramId: EFinancingProgram | undefined
  creditApplicationId: string | undefined
  deliveryOn: DateOnly
  paymentFrequency?: string
  isHighEstimate: boolean
  setValue: UseFormSetValue<T>
  getValues: UseFormGetValues<T>
}

export const useFirstPaymentDateOptions = <T extends FirstPaymentField>({
  financingProgramId,
  creditApplicationId,
  deliveryOn,
  paymentFrequency,
  isHighEstimate,
  setValue,
  getValues,
}: UseFirstPaymentDateOptionsParams<T>) => {
  const firstPaymentDateOptionsParams = useMemo(
    () => ({
      deliveryOn,
      systemOriginId: EOriginSystemId.Backoffice,
      isHighEstimate: isHighEstimate,
      paymentFrequency: paymentFrequency ?? EPaymentFrequency.Monthly,
    }),
    [deliveryOn, paymentFrequency, isHighEstimate],
  )

  const [firstPaymentDateOptions] = useGetFirstPaymentDateOptions(
    financingProgramId,
    creditApplicationId,
    firstPaymentDateOptionsParams,
  )

  useEffect(() => {
    const availableOptions = firstPaymentDateOptions ?? []
    if (availableOptions.length === 0) return
    const currentFirstPaymentDate = formatDate(new DateOnly(getValues('firstPaymentOn' as FieldPath<T>) ?? new Date()))
    const availableOptionsFormatted = availableOptions.map((d) => formatDate(d))
    if (!availableOptionsFormatted.includes(currentFirstPaymentDate)) {
      setValue('firstPaymentOn' as FieldPath<T>, formatDate(availableOptions[0]) as never)
    }
  }, [firstPaymentDateOptions, setValue, getValues])

  const minFirstPaymentDate = new DateOnly(firstPaymentDateOptions?.[0]).toDate()
  const maxFirstPaymentDate = new DateOnly(firstPaymentDateOptions?.[firstPaymentDateOptions.length - 1]).toDate()

  return {
    firstPaymentDateOptions,
    minFirstPaymentDate,
    maxFirstPaymentDate,
  }
}
